import { useState, useEffect, useMemo, useCallback } from "react";
import DeviceTable from "../../components/DeviceTable/DeviceTable";
import MenuItemHeader from "../../components/MenuItemHeader/MenuItemHeader";
import MobileDeviceTable from "../../components/MobileDeviceTable/MobileDeviceTable";
import Modal from "../../components/modal/modal";
import styles from "./Devices.module.scss";
import { useTranslation } from "react-i18next";
import ProcessingModal from "../../components/modals/ProcessingModal/ProcessingModal";
import { useAppSelector } from "../../store/hooks";
import { useGetDevicesQuery, useGetLicensesQuery, useGetSubscriptionsQuery } from "../../store/services/details-api";
import LoaderCard from "../../components/Loader/LoaderCard";
import ErrorCard from "../../components/ErrorCard/ErrorCard";
import { useToast } from "../../hooks/useToast";
import { trackEvent } from "../../utils/analytics";
import { EVENT_NAMES } from "../../types/event-names";
import { useLocation } from "react-router-dom";

const DevicesPage = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const location = useLocation();

  const authState = useAppSelector((state) => state.auth);
  const [isModalProcessingVisible, setIsModalProcessingVisible] = useState(false);
  const [isLoadingDeactivate, setIsLoadingDeactivate] = useState(false);
  const [deactivateSuccess, setDeactivateSuccess] = useState(false);

  const { data: devices, error, isFetching: isFetchingDevice, refetch } = useGetDevicesQuery(null);
  const { data: licenses, isFetching: licenseIsFetching, refetch: licenseRefetch } = useGetLicensesQuery(null);
  const { data: subscriptions, refetch: subsRefetch, isFetching: subsIsFetching } = useGetSubscriptionsQuery(null);

  const rerenderDevices = useCallback(async () => {
    await Promise.all([refetch(), subsRefetch(), licenseRefetch()]);
  }, [refetch, subsRefetch, licenseRefetch]);

  const devicesData = useMemo(() => {
    if (!devices || !subscriptions || !licenses) return [];

    const mergedData = devices.map((d) => ({
      ...d,
      ...subscriptions.find((item) => item.subscription_id === d.id),
      licenses: licenses.filter((item) => item.subscription_id === d.id),
      isExpired: licenses
        .filter((item) => item.subscription_id === d.id)
        .some((license) => new Date(license.expiration_date).getTime() < new Date().getTime()),
    }));

    // const isAnyActiveLicense = mergedData
    //   .filter(
    //     (device) => device.licenses.some((license) => !license.active) // Filter licenses with inactive status
    //   )
    //   .flatMap((device) =>
    //     device.licenses.filter((license) => !license.active).map((license) => license.subscription_id)
    //   );

    // const filteredDevices = mergedData.filter((device) => {
    //   // Check if device matches the criteria or if its subscription_id is in isAnyActiveLicense
    //   const isMatch = !(device.isCanceled === true && !device.next_payment);
    //   const hasActiveLicenseMatch = isAnyActiveLicense.includes(device?.subscription_id || "");

    //   // If the device has a matching subscription_id from any inactive license, it should be included
    //   return isMatch || hasActiveLicenseMatch;
    // });

    // const filteredDevices = mergedData.filter((device) => !(device.isCanceled === true && !device.next_payment));

    // Sort devices by expiration status (unexpired first)
    mergedData.sort((a, b) => Number(a.isExpired) - Number(b.isExpired));
    // Filter out expired devices - @Amit suggestion
    const filteredDevices = mergedData.filter((device) => !device.isExpired);

    return filteredDevices;
  }, [devices, licenses, subscriptions]);

  const isLoading = isFetchingDevice || subsIsFetching || licenseIsFetching;

  useEffect(() => {
    setIsLoadingDeactivate(false);
  }, []);

  useEffect(() => {
    if (deactivateSuccess) {
      showToast(t("Device deactivated successfully"));
    }
    // eslint-disable-next-line
  }, [deactivateSuccess]);

  useEffect(() => {
    trackEvent(EVENT_NAMES.SCREEN_DEVICES_VIEWED, { screen_name: "Devices" });
  }, []);

  useEffect(() => {
    // Re-fetch or reload data every time path changes
    rerenderDevices();
  }, [location.pathname, rerenderDevices]);

  return (
    <div className={styles.root}>
      <Modal
        isOpen={isModalProcessingVisible}
        setIsVisible={() => {
          setIsModalProcessingVisible(false);
        }}
      >
        <ProcessingModal setIsModalProcessingVisible={setIsModalProcessingVisible} />
      </Modal>

      <MenuItemHeader page={t("Devices")} subtitle={t("View protected devices, download apps and remove devices.")} />
      {isLoading ? (
        <LoaderCard />
      ) : error ? (
        <ErrorCard text={t("Unable to load data. Please refresh the page or try again later.")} />
      ) : devicesData.length > 0 ? (
        <>
          {devicesData?.length > 0 &&
            devicesData.map((elem) => (
              <div key={elem.id}>
                <DeviceTable
                  subNumber={elem.id}
                  title={elem.name}
                  number={`${elem.devices?.length ?? 0}/${elem.licenses?.length ?? elem.devices?.length}`}
                  data={elem.devices}
                  expired={authState.expired}
                  trial={false}
                  pause={elem.isPaused}
                  canceled={elem.isCanceled}
                  updateDevicesTable={rerenderDevices}
                  isLoadingDeactivate={isLoadingDeactivate}
                  setIsLoadingDeactivate={setIsLoadingDeactivate}
                  subscriptions={elem as any}
                  refreshData={rerenderDevices}
                  setDeactivateSuccess={setDeactivateSuccess}
                />
                <MobileDeviceTable
                  subNumber={elem.id}
                  title={elem.name}
                  number={`${elem.devices?.length ?? 0}/${elem.seats ?? elem.devices?.length}`}
                  data={elem.devices}
                  pause={elem.isPaused}
                  expired={authState.expired}
                  canceled={elem.isCanceled}
                  subscriptions={elem as any}
                  refreshData={rerenderDevices}
                  setDeactivateSuccess={setDeactivateSuccess}
                />
              </div>
            ))}
        </>
      ) : (
        <>
          <DeviceTable
            subNumber={"0"}
            number={""}
            data={[]}
            subscriptions={"" as any}
            refreshData={rerenderDevices}
            setDeactivateSuccess={setDeactivateSuccess}
          />
          <MobileDeviceTable
            subNumber={"0"}
            number={""}
            data={[]}
            subscriptions={"" as any}
            refreshData={rerenderDevices}
            setDeactivateSuccess={setDeactivateSuccess}
          />
        </>
      )}
    </div>
  );
};

export default DevicesPage;
